import { useEffect, useState } from "react";
import { Link, useSearch } from "wouter";
import { CheckCircle, XCircle, Loader2, Download, Shield, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SEO } from "@/components/seo";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

type Status = "verifying" | "success" | "error";
type SierraStep = "idle" | "saving" | "saved";

export default function SubscriptionSuccessPage() {
  const search = useSearch();
  const params = new URLSearchParams(search);
  const sessionId = params.get("session_id");
  const { toast } = useToast();

  const [status, setStatus] = useState<Status>("verifying");
  const [planName, setPlanName] = useState<string>("");
  const [errorMsg, setErrorMsg] = useState<string>("");

  // Sierra Chart account name
  const [sierraUsername, setSierraUsername] = useState("");
  const [sierraStep, setSierraStep] = useState<SierraStep>("idle");
  const [sierraError, setSierraError] = useState("");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      setErrorMsg("No session ID found. Please contact support.");
      return;
    }

    apiRequest("POST", "/api/subscriptions/verify", { sessionId })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || "Verification failed");
        setPlanName(data.planName ?? "");
        setStatus("success");
        // Pre-fill SC username if already set
        if (data.sierraUsername) setSierraUsername(data.sierraUsername);
      })
      .catch((err) => {
        setErrorMsg(err.message || "Failed to verify subscription");
        setStatus("error");
      });
  }, [sessionId]);

  async function saveSierraUsername() {
    const trimmed = sierraUsername.trim();
    if (!trimmed) {
      setSierraError("Please enter your Sierra Chart account name.");
      return;
    }
    setSierraStep("saving");
    setSierraError("");
    try {
      const res = await apiRequest("PUT", "/api/sierra/account", { sierraUsername: trimmed });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to save");
      setSierraStep("saved");
      toast({ title: "Sierra Chart account linked!", description: "Your access is being activated." });
    } catch (err: any) {
      setSierraStep("idle");
      setSierraError(err.message || "Failed to save account name.");
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <SEO title="Subscription Confirmed" description="Your subscription is active." noIndex />

      <div className="w-full max-w-md space-y-4">
        <Card>
          <CardContent className="p-8 text-center">
            {status === "verifying" && (
              <>
                <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
                <h1 className="text-xl font-bold mb-2">Activating your subscription…</h1>
                <p className="text-sm text-muted-foreground">
                  Please wait while we confirm your payment with Stripe.
                </p>
              </>
            )}

            {status === "success" && (
              <>
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <h1 className="text-2xl font-bold mb-2">You're subscribed!</h1>
                {planName && (
                  <p className="text-muted-foreground mb-6">
                    Welcome to <strong>{planName}</strong>. Your downloads are ready.
                  </p>
                )}
                <div className="flex flex-col gap-3">
                  <Link href="/purchases">
                    <Button className="w-full">
                      <Download className="w-4 h-4 mr-2" />
                      Go to My Downloads
                    </Button>
                  </Link>
                  <Link href="/">
                    <Button variant="outline" className="w-full">
                      Back to Home
                    </Button>
                  </Link>
                </div>
              </>
            )}

            {status === "error" && (
              <>
                <XCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
                <h1 className="text-xl font-bold mb-2">Something went wrong</h1>
                <p className="text-sm text-muted-foreground mb-6">
                  {errorMsg || "We couldn't verify your subscription. If you were charged, please contact support."}
                </p>
                <div className="flex flex-col gap-3">
                  <Link href="/pricing">
                    <Button className="w-full" variant="outline">
                      Back to Pricing
                    </Button>
                  </Link>
                  <Link href="/contact">
                    <Button variant="ghost" className="w-full">
                      Contact Support
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Sierra Chart account name setup — shown after successful subscription */}
        {status === "success" && (
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="p-6">
              <div className="flex items-start gap-3 mb-4">
                <Shield className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <h2 className="font-semibold text-sm">Link your Sierra Chart account</h2>
                  <p className="text-xs text-muted-foreground mt-1">
                    Required to activate your indicators in Sierra Chart. Enter the account name shown under{" "}
                    <strong>Help → About</strong> in Sierra Chart (labeled "Registered To:").
                  </p>
                </div>
              </div>

              {sierraStep === "saved" ? (
                <div className="flex items-center gap-2 text-green-600 text-sm font-medium">
                  <CheckCircle className="w-4 h-4" />
                  Linked! Sierra Chart access is being activated.
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="scUsername" className="text-xs">Sierra Chart Account Name</Label>
                    <Input
                      id="scUsername"
                      value={sierraUsername}
                      onChange={(e) => setSierraUsername(e.target.value)}
                      placeholder="e.g. JOHN12345"
                      className="mt-1 uppercase"
                      onKeyDown={(e) => e.key === "Enter" && saveSierraUsername()}
                    />
                    {sierraError && (
                      <p className="text-xs text-destructive mt-1">{sierraError}</p>
                    )}
                  </div>
                  <Button
                    onClick={saveSierraUsername}
                    disabled={sierraStep === "saving"}
                    className="w-full"
                    size="sm"
                  >
                    {sierraStep === "saving" ? (
                      <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> Activating…</>
                    ) : (
                      <><ChevronRight className="w-3 h-3 mr-1" /> Activate Sierra Chart Access</>
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground text-center">
                    You can also do this later in{" "}
                    <Link href="/settings" className="underline">Account Settings</Link>.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
